import { useState } from 'react';
import { formatMoney, formatDate } from '../lib/money';

export default function PoCard({ po, invoices, helpers, actions, children, firstPoAttachment }) {
  const [expanded, setExpanded] = useState(false);

  const { paidFor } = helpers;
  const isEmpty = invoices.length === 0;

  const poDate = po?.order_date || (po?.created_at ? po.created_at.slice(0, 10) : null);
  const poValue = Number(po?.total_amount || 0);
  const invoicedTotal = invoices.reduce((s, i) => s + Number(i.total || 0), 0);
  const approvedTotal = invoices.reduce((s, i) => s + Number(i.amount_approved || 0), 0);
  const paidTotal = invoices.reduce((s, i) => s + paidFor(i.id), 0);
  const outstandingTotal = (approvedTotal > 0 ? approvedTotal : invoicedTotal) - paidTotal;
  const pctPaid = poValue > 0 ? Math.round((paidTotal / poValue) * 1000) / 10 : 0;
  const pctColor = pctPaid >= 100 ? '#036e43' : pctPaid > 0 ? '#b45309' : 'var(--text-subdued)';

  return (
    <div className={`po-card ${expanded ? 'po-card-expanded' : ''}`}>
      <div className="po-card-header" onClick={() => setExpanded(e => !e)}>
        <span className={`expander ${expanded ? '' : 'rotated'}`}>▾</span>
        <span className="po-card-left">
          <span className="po-label">
            {/^PO/i.test(po?.po_number || '') ? po.po_number : `PO #${po?.po_number || '—'}`}
            {po?.locked && <span className="lock-icon" title={`Locked by ${po.locked_by || 'admin'}`}> &#x1F512;</span>}
          </span>
          {firstPoAttachment && (
            <button type="button" className="po-eye-btn" onClick={e => { e.stopPropagation(); actions.onPreviewFile?.(firstPoAttachment); }} title="Preview PO file">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M1 8s2.5-5 7-5 7 5 7 5-2.5 5-7 5-7-5-7-5z" stroke="currentColor" strokeWidth="1.5"/><circle cx="8" cy="8" r="2" stroke="currentColor" strokeWidth="1.5"/></svg>
            </button>
          )}
          <span className="po-sep">·</span>
          <span className="po-meta">{po?.supplier_name || '—'}</span>
          {poDate && <><span className="po-sep">·</span><span className="po-meta">Issued {formatDate(poDate)}</span></>}
          {po?.tr_number && <><span className="po-sep">·</span><span className="po-meta" title="Skutopia Transfer Number">TR {po.tr_number}</span></>}
          {po?.packing_slip_number && <><span className="po-sep">·</span><span className="po-meta" title="Packing Slip Number">PS {po.packing_slip_number}</span></>}
          {isEmpty && <><span className="po-sep">·</span><span className="po-meta">No invoices yet</span></>}
          {!isEmpty && <><span className="po-sep">·</span><span className="po-meta">{invoices.length} invoice{invoices.length === 1 ? '' : 's'}</span></>}
        </span>
        <span className="po-card-actions" onClick={e => e.stopPropagation()}>
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => actions.onEditPo?.(po)}>Edit PO</button>
          <button type="button" className="btn btn-primary btn-sm" onClick={() => actions.onLogInvoiceForPo?.(po)}>+ Log Invoice</button>
        </span>
      </div>

      {expanded && (
        <>
          <div className="po-card-body">
            <div className="po-card-stats">
              <div className="po-card-stat">
                <div className="po-card-stat-label">PO Value</div>
                <div className="po-card-stat-value">{formatMoney(poValue)}</div>
              </div>
              <div className="po-card-stat">
                <div className="po-card-stat-label">Invoiced</div>
                <div className="po-card-stat-value">{formatMoney(invoicedTotal)}</div>
              </div>
              <div className="po-card-stat">
                <div className="po-card-stat-label">Paid</div>
                <div className="po-card-stat-value">{formatMoney(paidTotal)}</div>
              </div>
              <div className="po-card-stat">
                <div className="po-card-stat-label">Outstanding</div>
                <div className="po-card-stat-value">{formatMoney(Math.max(0, outstandingTotal))}</div>
              </div>
              <div className="po-card-stat">
                <div className="po-card-stat-label">% Paid</div>
                <div className="po-card-stat-value" style={{ color: pctColor }}>{pctPaid}%</div>
              </div>
            </div>
          </div>

          {invoices.length > 0 && (
            <div className="invoice-table-head" role="row">
              <div data-col="dot" />
              <div data-col="invoice">Inv #</div>
              <div data-col="ps">PS #</div>
              <div data-col="tr">TR #</div>
              <div data-col="inv-date">Invoice Date</div>
              <div data-col="aging">Aging</div>
              <div data-col="total">Total</div>
              <div data-col="paid">Paid</div>
              <div data-col="approved">Approved</div>
              <div data-col="status">Status</div>
              <div data-col="notes" />
              <div data-col="actions">Actions</div>
            </div>
          )}
          {children}
        </>
      )}
    </div>
  );
}
